import { useState } from 'react';
import { Button, Modal } from 'equasion-lib';
import { FiMinus, FiPlus } from 'react-icons/fi';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import type { FoundStock, ZoneCountProductInfo } from '../zoneCountSteps';

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

export type FoundStockDraft = {
  /** Set when editing an entry already on the count. */
  id?: string;
  product: ZoneCountProductInfo | null;
  code?: string | null;
  lot?: string | null;
  /** YYYY-MM-DD */
  expiration?: string | null;
  quantity?: number;
};

type ZoneCountFoundStockSheetProps = {
  initial: FoundStockDraft;
  zoneName: string;
  lookupProduct: (code: string) => Promise<ZoneCountProductInfo | null>;
  onSave: (stock: Omit<FoundStock, 'id'> & { id?: string }) => void;
  onRemove?: (id: string) => void;
  onClose: () => void;
};

const isFda = (product: ZoneCountProductInfo | null) => !!product?.fdaProductId;

/**
 * Stock counted on the shelf by number (OTC, UPC, an Rx lot without serials) of a lot the zone has
 * no record of. The product comes from its barcode — scanned on the count screen, or typed here —
 * and an FDA product needs its lot and expiration, so the record names exactly what was found. A
 * lot that turns out to be one the zone does list is added to that lot's count instead.
 */
function ZoneCountFoundStockSheet({
  initial,
  zoneName,
  lookupProduct,
  onSave,
  onRemove,
  onClose
}: ZoneCountFoundStockSheetProps) {
  const [product, setProduct] = useState<ZoneCountProductInfo | null>(initial.product);
  const [code, setCode] = useState(initial.code || '');
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const [lot, setLot] = useState(initial.lot || '');
  const [expiration, setExpiration] = useState(initial.expiration || '');
  const [quantity, setQuantity] = useState(String(initial.quantity ?? 1));

  const needsLot = isFda(product);
  const qty = Number(quantity);
  const qtyValid = /^\d+$/.test(quantity.trim()) && qty >= 1;
  const ready =
    !!product && qtyValid && (!needsLot || (!!lot.trim() && /^\d{4}-\d{2}-\d{2}$/.test(expiration)));

  const find = async () => {
    const text = code.trim();
    if (!text || finding) return;
    setFinding(true);
    setFindError(null);
    try {
      const found = await lookupProduct(text);
      if (found) setProduct(found);
      else setFindError(`No product in the catalog matches “${text}”.`);
    } catch (e) {
      setFindError(operatorFacingMessage(e, 'Could not look that up. Check the connection and try again.'));
    } finally {
      setFinding(false);
    }
  };

  const save = () => {
    if (!ready || !product) return;
    onSave({
      id: initial.id,
      product,
      lot: lot.trim() || null,
      expiration: expiration || null,
      quantity: qty
    });
  };

  return (
    <Modal
      className="zc-modal"
      modalOpen
      setModalOpen={(nextOpen?: boolean) => {
        if (!nextOpen) onClose();
      }}
    >
      <form
        className="zc-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (product) save();
          else find();
        }}
      >
        <strong className="zc-form__title">
          {initial.id ? 'Edit found stock' : 'Found stock'}
        </strong>
        <p className="zc-form__text">
          Stock on the shelf in <strong>{zoneName}</strong> that the zone has no record of. It is
          counted over the record, and the review can quarantine it.
        </p>

        {product ? (
          <div className="zc-form__product">
            <WarehouseProductDisplay
              compact
              product={{
                title: product.productName,
                fdaProduct:
                  product.ndc || product.deaSchedule
                    ? { elevenDigitNdc: product.ndc, deaSchedule: product.deaSchedule }
                    : null,
                upcProduct: product.upc ? { upc: product.upc } : null
              }}
            />
            {!initial.product && (
              <button type="button" className="zc-link" onClick={() => setProduct(null)}>
                Change product
              </button>
            )}
            {product.kind === 'SERIALIZED' && (
              <p className="zc-form__hint">
                This product is usually serialized. If the packs carry a 2D barcode, scan each one
                on the count instead.
              </p>
            )}
          </div>
        ) : (
          <>
            <label className="zc-field">
              <span>NDC / UPC / GTIN</span>
              <input
                name="code"
                autoFocus
                autoComplete="off"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            {findError && <p className="zc-form__error">{findError}</p>}
            <Button
              type="primary"
              fullWidth
              isSubmit
              disabled={!code.trim() || finding}
            >
              {finding ? 'Looking up…' : 'Find product'}
            </Button>
          </>
        )}

        {product && (
          <>
            <div className="zc-form__row">
              <label className="zc-field">
                <span>Lot{needsLot ? '' : ' (optional)'}</span>
                <input
                  name="lot"
                  autoComplete="off"
                  autoCapitalize="characters"
                  value={lot}
                  onChange={(e) => setLot(e.target.value)}
                />
              </label>
              <label className="zc-field">
                <span>Expiration{needsLot ? '' : ' (optional)'}</span>
                <input
                  name="expiration"
                  type="date"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                />
              </label>
            </div>
            <label className="zc-field">
              <span>Units counted</span>
              <div className="zc-bulk__stepper">
                <button
                  type="button"
                  aria-label="One less"
                  disabled={!qtyValid || qty <= 1}
                  onClick={() => setQuantity(String(Math.max(1, qty - 1)))}
                >
                  <FiMinus />
                </button>
                <input
                  name="quantity"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
                <button
                  type="button"
                  aria-label="One more"
                  onClick={() => setQuantity(String((qtyValid ? qty : 0) + 1))}
                >
                  <FiPlus />
                </button>
              </div>
            </label>
            {needsLot && !ready && qtyValid && (
              <p className="zc-form__hint">Enter the lot and expiration printed on the pack.</p>
            )}
            <Button type="primary" fullWidth isSubmit disabled={!ready}>
              {initial.id ? 'Save' : 'Add to count'}
            </Button>
          </>
        )}
        {initial.id && onRemove && (
          <Button type="danger" fullWidth {...click(() => onRemove(initial.id!))}>
            Take off the count
          </Button>
        )}
        <Button type="secondary" fullWidth {...click(onClose)}>
          Cancel
        </Button>
      </form>
    </Modal>
  );
}

export default ZoneCountFoundStockSheet;
